import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api/client';
import type { EvalRun, EvalCaseResult } from '../api/types';
import { renderSlidesDoc } from '../lib/render-slides';
import { setPageTitle } from '../lib/page-title';
import type { SlideDeck } from '../lib/slide-model';

/**
 * Eval dashboard (/eval). Lists evaluation-harness runs, shows the selected
 * run's aggregate + per-case scores, a trend across recent runs, and an
 * in-place deck preview for any case (reusing renderDeck → srcdoc, the same
 * recipe deck-analytics uses). Internal: any signed-in user can view.
 *
 * Light DOM so the global stylesheet applies; a scoped <style> adds the
 * table/preview chrome specific to this page.
 */
@customElement('eval-page')
export class EvalPage extends LitElement {
  @state() private runs: EvalRun[] | null = null;
  @state() private selected: EvalRun | null = null;
  @state() private error: string | null = null;
  @state() private previewCaseId: string | null = null;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    setPageTitle('Eval');
    void this.load();
  }

  private async load() {
    this.error = null;
    try {
      this.runs = await api.listEvalRuns();
      if (this.runs.length > 0) {
        await this.selectRun(this.runs[0].id);
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load eval runs';
      this.runs = [];
    }
  }

  private async selectRun(id: string) {
    this.previewCaseId = null;
    try {
      this.selected = await api.getEvalRun(id);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load run';
    }
  }

  private pct(n: number): string {
    return `${Math.round(n * 100)}%`;
  }

  render() {
    return html`
      ${this.styles()}
      <section class="page">
        <div class="page-head">
          <div class="titles">
            <h1>Evaluation</h1>
            <p class="page-sub">Deck-generation quality — deterministic · render-fit · judge</p>
          </div>
        </div>
        ${this.renderBody()}
      </section>
    `;
  }

  private renderBody() {
    if (this.error) return html`<div class="state error">Couldn't load — ${this.error}</div>`;
    if (this.runs === null) return html`<div class="state">Loading runs…</div>`;
    if (this.runs.length === 0)
      return html`<div class="state">
        <p>No evaluation runs yet.</p>
        <p class="page-sub">Run <code>go run ./scripts/eval --suite golden --judge</code> to create one.</p>
      </div>`;
    return html`
      ${this.renderTrend()}
      ${this.renderRunPicker()}
      ${this.selected ? this.renderRun(this.selected) : html`<div class="state">Select a run.</div>`}
    `;
  }

  private renderTrend() {
    // Oldest→newest sparkline of det-pass-rate + avg judge across recent runs.
    const runs = [...(this.runs ?? [])].reverse();
    return html`
      <div class="ev-trend">
        ${runs.map(
          (r) => html`<div class="ev-trend-col" title=${`${r.id} · det ${this.pct(r.detPassRate)} · judge ${r.judgeOverall?.toFixed(1) ?? '—'}`}>
            <div class="ev-bar det" style=${`height:${Math.max(2, r.detPassRate * 60)}px`}></div>
            <div class="ev-bar judge" style=${`height:${Math.max(2, ((r.judgeOverall ?? 0) / 5) * 60)}px`}></div>
          </div>`,
        )}
        <div class="ev-trend-key"><span class="k det"></span>det pass <span class="k judge"></span>judge/5</div>
      </div>
    `;
  }

  private renderRunPicker() {
    return html`
      <div class="ev-runs">
        ${(this.runs ?? []).map(
          (r) => html`<button
            class=${`ev-run ${this.selected?.id === r.id ? 'active' : ''}`}
            @click=${() => this.selectRun(r.id)}
          >
            <strong>${r.id}</strong>
            <span>det ${this.pct(r.detPassRate)} · scale ${r.avgMinScale.toFixed(2)}${r.judged ? html` · judge ${r.judgeOverall?.toFixed(1)}` : ''}</span>
          </button>`,
        )}
      </div>
    `;
  }

  private renderRun(run: EvalRun): TemplateResult {
    const d = run.data;
    const agg = d?.aggregate;
    return html`
      <div class="ev-agg">
        <div><b>${agg?.deterministicPass ?? run.deterministicPass}/${agg?.cases ?? run.cases}</b>deterministic pass</div>
        <div><b>${(agg?.avgMinScale ?? run.avgMinScale).toFixed(2)}</b>avg min-scale</div>
        <div><b>${agg?.overpackedCases ?? run.overpackedCases}</b>overpacked</div>
        ${run.judged ? html`<div><b>${run.judgeOverall?.toFixed(2) ?? '—'}</b>judge (overall)</div>` : ''}
        ${agg && agg.errors > 0 ? html`<div class="bad"><b>${agg.errors}</b>errors</div>` : ''}
        <div class="ev-meta">prompt ${run.promptHash ?? '—'} · git ${run.gitSha ?? '—'} · ${run.timestamp}</div>
      </div>
      ${d ? this.renderCases(d.cases) : html`<div class="state">No per-case detail stored.</div>`}
      ${this.renderPreview(d?.cases ?? [])}
    `;
  }

  private renderCases(cases: EvalCaseResult[]) {
    return html`
      <table class="ev-table">
        <tr><th>Case</th><th>Det.</th><th>Min scale</th><th>Judge</th><th>Notes</th><th></th></tr>
        ${cases.map((c) => this.renderCaseRow(c))}
      </table>
    `;
  }

  private renderCaseRow(c: EvalCaseResult) {
    const failing = (c.checks ?? []).filter((ch) => !ch.pass);
    const mustFail = failing.filter((ch) => ch.severity === 'must');
    const minScale = c.renderFit ? c.renderFit.minScale : null;
    const scaleBad = minScale !== null && minScale < 0.75;
    return html`
      <tr>
        <td>${c.id}</td>
        <td class=${c.deterministicPass ? 'ok' : 'bad'}>${c.deterministicPass ? 'pass' : 'FAIL'}</td>
        <td class=${scaleBad ? 'bad' : ''}>${minScale !== null ? minScale.toFixed(2) : '—'}</td>
        <td>${c.judge ? ((c.judge.fit + c.judge.design + c.judge.flow + c.judge.grounding) / 4).toFixed(1) : '—'}</td>
        <td class="ev-notes">
          ${c.error ? html`<span class="bad">${c.error}</span>` : ''}
          ${mustFail.map((ch) => html`<span class="chip bad">${ch.name}</span>`)}
          ${failing.filter((ch) => ch.severity === 'should').map((ch) => html`<span class="chip">${ch.name}</span>`)}
          ${c.judge?.rationale ? html`<span class="ev-rat">“${c.judge.rationale}”</span>` : ''}
        </td>
        <td>${c.model ? html`<button class="cell-action" @click=${() => (this.previewCaseId = this.previewCaseId === c.id ? null : c.id)}>${this.previewCaseId === c.id ? 'Hide' : 'Preview'}</button>` : ''}</td>
      </tr>
    `;
  }

  private renderPreview(cases: EvalCaseResult[]) {
    if (!this.previewCaseId) return html``;
    const c = cases.find((x) => x.id === this.previewCaseId);
    if (!c?.model) return html``;
    let srcdoc = '';
    try {
      // The eval `model` is typed DeckModel but at runtime is a V2 SlideDeck
      // (schemaVersion 2); render it with the V2 renderer.
      srcdoc = renderSlidesDoc(c.model as unknown as SlideDeck);
    } catch {
      return html`<div class="state error">Could not render preview.</div>`;
    }
    return html`
      <div class="ev-preview">
        <div class="ev-preview-head">${c.id}</div>
        <div class="ev-preview-frame">
          <iframe .srcdoc=${srcdoc} title=${`preview ${c.id}`}></iframe>
        </div>
      </div>
    `;
  }

  private styles() {
    return html`<style>
      .ev-trend { display: flex; align-items: flex-end; gap: 6px; height: 76px; margin: 8px 0 20px; padding: 8px 12px; background: var(--surface, #fff); border-radius: 10px; overflow-x: auto; }
      .ev-trend-col { display: flex; gap: 2px; align-items: flex-end; height: 60px; }
      .ev-bar { width: 5px; border-radius: 2px; }
      .ev-bar.det { background: #3f7a55; }
      .ev-bar.judge { background: #2c5470; }
      .ev-trend-key { margin-left: auto; align-self: center; font-size: 12px; color: var(--text-tertiary, #888); display: flex; align-items: center; gap: 4px; }
      .ev-trend-key .k { width: 10px; height: 10px; border-radius: 2px; display: inline-block; margin-left: 8px; }
      .ev-trend-key .k.det { background: #3f7a55; } .ev-trend-key .k.judge { background: #2c5470; }
      .ev-runs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
      .ev-run { text-align: left; padding: 8px 12px; border: 1px solid var(--border-default, #ccc); border-radius: 8px; background: var(--surface, #fff); cursor: pointer; display: flex; flex-direction: column; gap: 2px; }
      .ev-run.active { border-color: #1e2a3a; box-shadow: 0 0 0 1px #1e2a3a; }
      .ev-run strong { font-size: 13px; } .ev-run span { font-size: 11px; color: var(--text-tertiary, #888); }
      .ev-agg { display: flex; gap: 18px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
      .ev-agg > div { background: var(--surface, #fff); padding: 10px 14px; border-radius: 8px; font-size: 12px; color: var(--text-tertiary, #888); }
      .ev-agg b { display: block; font-size: 22px; color: var(--text-primary, #1a1a1a); }
      .ev-agg .bad b { color: #a03020; }
      .ev-agg .ev-meta { background: none; font-size: 11px; }
      .ev-table { width: 100%; border-collapse: collapse; background: var(--surface, #fff); border-radius: 10px; overflow: hidden; }
      .ev-table th, .ev-table td { padding: 8px 12px; border-bottom: 1px solid var(--border-subtle, #eee); text-align: left; font-size: 13px; vertical-align: top; }
      .ev-table th { background: #1e2a3a; color: #fff; font-weight: 500; }
      .ev-table .ok { color: #3f7a55; font-weight: 600; } .ev-table .bad { color: #a03020; font-weight: 600; }
      .ev-notes { font-size: 12px; }
      .chip { display: inline-block; padding: 1px 6px; margin: 1px; border-radius: 4px; background: var(--surface-hover, #eee); color: var(--text-secondary, #555); font-size: 11px; }
      .chip.bad { background: #f2e1de; color: #a03020; }
      .ev-rat { display: block; color: var(--text-tertiary, #888); font-style: italic; margin-top: 2px; }
      .cell-action { font-size: 12px; padding: 3px 8px; border: 1px solid var(--border-default, #ccc); border-radius: 6px; background: var(--surface, #fff); cursor: pointer; }
      .ev-preview { margin-top: 16px; }
      .ev-preview-head { font-size: 12px; color: var(--text-tertiary, #888); margin-bottom: 6px; }
      .ev-preview-frame { width: 100%; max-width: 960px; aspect-ratio: 16 / 9; border-radius: 10px; overflow: hidden; border: 1px solid var(--border-default, #ccc); }
      .ev-preview-frame iframe { width: 1920px; height: 1080px; border: 0; transform: scale(0.5); transform-origin: top left; }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eval-page': EvalPage;
  }
}
